import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'

/**
 * The inline action chip — the small committing control that lives in a card
 * header, on a row, or in a sheet footer. Anything larger is a `Button`;
 * anything that isn't an action at all is a `StatusChip`.
 *
 * Three weights:
 *   bare      just the label, 600 11px accent. For "Edit", "Show all 8" —
 *             actions that sit inside a header row and shouldn't build a wall
 *             of chrome next to the content they act on.
 *   quiet     the same label on a contained fill. The secondary beside a
 *             primary, and any action that needs to look pressable on its own.
 *   primary   ember. Reserved for the one action that commits the sheet, the
 *             wizard step, or the banner.
 *
 * Sizes follow the prototype exactly: sm is a chip inside a card, md a chip in
 * a banner or a row, lg the footer primary.
 */

export type ActionTone = 'bare' | 'quiet' | 'primary'
export type ActionSize = 'sm' | 'md' | 'lg'

const SIZES: Record<ActionSize, CSSProperties> = {
  sm: { padding: '6px 11px', borderRadius: 8, font: '600 11px Manrope,sans-serif' },
  md: { padding: '7px 12px', borderRadius: 8, font: '600 11px Manrope,sans-serif' },
  lg: { padding: '9px 15px', borderRadius: 9, font: '600 12px Manrope,sans-serif' },
}

interface ActionProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  tone?: ActionTone
  size?: ActionSize
  /**
   * The label colour for a quiet chip: accent when it is the thing to do,
   * muted when it is the alternative to the thing to do ("Retry now" beside
   * "Update card"), danger when it destroys something. Ignored by the other two
   * tones, which own their colour.
   *
   * `danger` is not a new treatment: it is exactly Button's `dangerOutline`,
   * which is .b3dq plus .w-danger-ink — the same chip this already is, in
   * failure ink, with the rim that class brings on hover. Shaped like every
   * other chip in the row so it does not shout, coloured so there is no
   * mistaking what it does.
   */
  quietTone?: 'accent' | 'muted' | 'danger'
  children: ReactNode
  style?: CSSProperties
}

export function Action({
  tone = 'quiet',
  size = 'sm',
  quietTone = 'accent',
  children,
  className = '',
  style,
  ...props
}: ActionProps) {
  const danger = tone === 'quiet' && quietTone === 'danger'
  const cls = `${tone === 'primary' ? 'b3d' : tone === 'quiet' ? 'b3dq' : ''}${danger ? ' w-danger-ink' : ''}`

  const colour =
    tone === 'primary'
      ? 'var(--color-amber-ink)'
      : danger
        ? 'var(--w-danger-ink)'
        : tone === 'quiet' && quietTone === 'muted'
          ? 'var(--color-muted)'
          : 'var(--color-action-ink)'

  return (
    <button
      type="button"
      className={`tab ${cls} ${className}`}
      style={{
        ...SIZES[size],
        // A bare action is type, not a control: no padding, no fill, nothing
        // for the eye to read as a box.
        ...(tone === 'bare' ? { padding: 0, borderRadius: 0 } : null),
        color: colour,
        border: 0,
        // Never `background`, and never a fill: the shorthand resets
        // background-image and takes .b3d's gradient with it, and .b3dq owns
        // its own --w-fill. Only the bare tone has nothing to inherit.
        ...(tone === 'bare' ? { backgroundColor: 'transparent' } : null),
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flex: 'none',
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  )
}
